/**
 * The portfolio page's filters, as one rule the page and MCP both apply: a
 * portfolio company is small enough a list (a hundred and some) that the page
 * filters what it already holds, and the tool filters the same rows the same
 * way rather than through a second, SQL-shaped reading of the words.
 */
export function matchesPortfolioFilters(company, filters) {
    if (filters.status !== undefined && filters.status.length > 0) {
        if (!filters.status.includes(company.status))
            return false;
    }
    if (filters.holdingFunds !== undefined && filters.holdingFunds.length > 0) {
        const wanted = filters.holdingFunds;
        if (!company.holdingFunds.some((code) => wanted.includes(code)))
            return false;
    }
    if (filters.sectorTags !== undefined && filters.sectorTags.length > 0) {
        const wanted = filters.sectorTags.map((tag) => tag.toLowerCase());
        if (!company.sectorTags.some((tag) => wanted.includes(tag.toLowerCase())))
            return false;
    }
    const coInvestor = filters.coInvestor?.trim().toLowerCase() ?? '';
    if (coInvestor !== '') {
        const found = company.coInvestors.some((row) => row.fundId === filters.coInvestor || row.fundName.toLowerCase() === coInvestor);
        if (!found)
            return false;
    }
    const text = filters.text?.trim().toLowerCase() ?? '';
    if (text !== '') {
        const haystack = [
            company.name,
            ...company.aliases,
            ...company.coInvestors.map((row) => row.fundName),
        ];
        if (!haystack.some((value) => value.toLowerCase().includes(text)))
            return false;
    }
    return true;
}
/** `YYYY-MM`, the precision a round is added at. */
export const ROUND_MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
/**
 * Round labels that are not a raise: Dealroom files an acquisition, a merger,
 * a buyout, a bankruptcy, a secondary or a spinout as a round. The database's
 * `is_raise_round` is the same list, held to this one by a test.
 */
export const NOT_A_RAISE_ROUND_LABELS = [
    'ACQUISITION',
    'MERGER',
    'BUYOUT',
    'BANKRUPTCY',
    'SECONDARY',
    'POST IPO SECONDARY',
    'SPINOUT',
    'CORPORATE SPINOUT',
];
const NOT_A_RAISE = new Set(NOT_A_RAISE_ROUND_LABELS);
/** A round with no label is counted: a raise nobody named is still a raise. */
export function isRaiseRound(label) {
    return label === null || !NOT_A_RAISE.has(label.trim().toUpperCase());
}
/** Whole months from `YYYY-MM-DD` to `today`, as the database's `months_since` counts them. */
export function monthsSince(day, today = new Date()) {
    const [year, month, date] = day.split('-').map(Number);
    if (year === undefined || month === undefined || date === undefined)
        return 0;
    let months = (today.getFullYear() - year) * 12 + (today.getMonth() + 1 - month);
    if (today.getDate() < date)
        months -= 1;
    return Math.max(0, months);
}
/**
 * "14 mo ago", "3 yr ago" past two years, "this month" under one. A raise
 * dated only to its year is said as the year ("2023"): counting months from
 * 1 January would be a claim the date does not make.
 */
export function timeSinceRaiseText(raise, today = new Date()) {
    if (raise.datePrecision === 'year')
        return raise.raisedOn.slice(0, 4);
    const months = monthsSince(raise.raisedOn, today);
    if (months === 0)
        return 'this month';
    if (months < 24)
        return `${months} mo ago`;
    return `${Math.floor(months / 12)} yr ago`;
}
//# sourceMappingURL=portfolio.js.map